import React from "react";
import { Box, Typography, alpha, useTheme } from "@mui/material";
import CircleIcon from "@mui/icons-material/Circle";
import { formatDistanceToNow } from "date-fns/formatDistanceToNow";
import useWorkspaceNavigation from "../../hooks/useWorkspaceNavigation";
import { NotificationSummaryDto } from "../../shared/dtos/notification/NotificationSummaryDto";
import { NotificationResponseDto } from "../../shared/dtos/notification/NotificationResponseDto";

interface NotificationUserCardProps {
  notification: NotificationSummaryDto | NotificationResponseDto;
  onRead: (id: string) => void;
}

const NotificationUserCard: React.FC<NotificationUserCardProps> = ({
  notification,
  onRead,
}) => {
  const theme = useTheme();
  const { navigateToTab } = useWorkspaceNavigation();

  const handleAction = (): void => {
    if (!notification.isRead) {
      onRead(notification.id);
    }

    if (notification.relatedEntityId && notification.relatedContext) {
      let path = "";
      switch (notification.relatedContext) {
        case "PURCHASE_ORDER":
          path = `/purchase-orders/view/${notification.relatedEntityId}`;
          break;
        case "INVOICE":
          path = `/invoices/view/${notification.relatedEntityId}`;
          break;
        case "CREDIT_NOTE":
          path = `/credit-notes/view/${notification.relatedEntityId}`;
          break;
        case "DEBT":
          path = `/debts/view/${notification.relatedEntityId}`;
          break;
        default:
          return;
      }
      navigateToTab(path);
    }
  };

  return (
    <Box
      onClick={handleAction}
      sx={{
        p: 2,
        cursor: "pointer",
        borderBottom: `1px solid ${theme.palette.divider}`,
        bgcolor: notification.isRead
          ? "transparent"
          : alpha(theme.palette.secondary.main, 0.05),
        "&:hover": { bgcolor: alpha(theme.palette.action.hover, 0.08) },
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
      }}
    >
      {!notification.isRead && (
        <CircleIcon sx={{ fontSize: 10, mt: 0.8, color: "secondary.main" }} />
      )}
      <Box sx={{ flexGrow: 1 }}>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: notification.isRead ? 500 : 700 }}
        >
          {notification.subject}
        </Typography>

        {notification.subject && (
          <Typography variant="body2" sx={{ mb: 0.5, color: "text.secondary" }}>
            {notification.subject}
          </Typography>
        )}

        <Typography variant="caption" sx={{ color: "text.disabled" }}>
          {formatDistanceToNow(new Date(notification.createdAt), {
            addSuffix: true,
          })}
        </Typography>
      </Box>
    </Box>
  );
};

export default NotificationUserCard;
